import { Button, Label, Modal, TextInput, Datepicker, Select, Textarea, Carousel } from 'flowbite-react';
import { useState } from 'react';
import useAchievementController from '../controllers/useAchievementController';
import { useSession } from '../context/SessionContext';

function Achievement() {
  const { session } = useSession();
  const [openModal, setOpenModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentAchievement, setCurrentAchievement] = useState(null);
  const [date, setDate] = useState(new Date());
  const [formData, setFormData] = useState({
    name: '',
    organizer: '',
    date: '',
    description: '',
    level: '',
    certificate_link: '',
  });
  const [images, setImages] = useState([]);
  const [imageError, setImageError] = useState('');

  const {
    achievements,
    loading,
    error,
    addAchievement,
    updateAchievement,
    deleteAchievement,
    deleteImage,
  } = useAchievementController();

  const onCloseModal = () => {
    setOpenModal(false);
    setIsEditMode(false);
    setCurrentAchievement(null);
    setImages([]);
    setImageError('');
    setFormData({
      name: '',
      organizer: '',
      date: '',
      description: '',
      level: '',
      certificate_link: '',
    });
    setDate(new Date());
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleImageChange = (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      const validFiles = [];
      for (const file of files) {
        if (file.size > 500 * 1024) {
          setImageError('Each image must be less than 500KB');
          return;
        }
        validFiles.push(file);
      }
      setImages((prevImages) => [...prevImages, ...validFiles]);
      setImageError('');
      e.target.value = '';
    }
  };

  const handleRemoveImage = async (image, index) => {
    try {
      if (isEditMode && image.path) {
        await deleteImage(image.path);
        setCurrentAchievement(prev => ({
          ...prev,
          images: prev.images.filter(img => img.path !== image.path)
        }));
      }
      setImages(prev => prev.filter((_, i) => i !== index));
    } catch (error) {
      console.error('Error deleting image:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.organizer || !formData.level) {
      alert('Please fill in all required fields: Name, Organizer, and Level.');
      return;
    }

    if (images.length > 0 && imageError) {
      alert('Please fix image errors before submitting');
      return;
    }

    try {
      const achievementData = {
        ...formData,
        date: date.toISOString(),
        description: formData.description || null,
        certificate_link: formData.certificate_link || null,
      };

      if (isEditMode && currentAchievement) {
        const newImageFiles = images.filter(img => img instanceof File);
        await updateAchievement(currentAchievement.id, achievementData, newImageFiles);
      } else {
        await addAchievement(achievementData, images);
      }

      onCloseModal();
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const handleEdit = (achievement) => {
    setCurrentAchievement(achievement);
    setIsEditMode(true);
    setFormData({
      name: achievement.name,
      organizer: achievement.organizer,
      date: achievement.date,
      description: achievement.description || '',
      level: achievement.level,
      certificate_link: achievement.certificate_link || '',
    });
    setDate(new Date(achievement.date));
    setImages(achievement.images || []);
    setOpenModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this achievement?')) {
      await deleteAchievement(id);
    }
  };

  // Sort achievements by date (newest first)
  const sortedAchievements = [...achievements].sort((a, b) => 
    new Date(b.date) - new Date(a.date)
  );

  return (
    <div className="flex">
      <section className="flex-1 py-5 md:px-5 bg-gray-100 dark:bg-gray-900">
        <div className="grid md:grid-cols-3 grid-cols-2">
          <div className="my-6 md:col-span-2 md:pl-6">
            <h1 className="text-3xl font-extrabold text-left lg:text-5xl 2xl:text-8xl">
              <span className="text-transparent bg-gradient-to-br bg-clip-text from-sky-800 via-sky-600 to-sky-700">
                Achievements
              </span>
            </h1>
            <p className="max-w-3xl md:mt-6 mt-2 mr-4 md:text-lg text-sm text-left text-gray-700 dark:text-white">
              Showcase your notable accomplishments, awards, and recognitions.
            </p>
          </div>
          <div className="mx-auto my-auto md:col-span-1">
            <Button color="blue" onClick={() => setOpenModal(true)}>
              <b>+</b> Add Achievement
            </Button>
          </div>
        </div>

        {/* Achievements List */}
        <div className="mt-6">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : error ? (
            <div className="p-4 mb-4 text-sm text-red-700 bg-red-100 rounded-lg dark:bg-red-200 dark:text-red-800">
              Error: {error}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {sortedAchievements.map((ach) => (
                <div 
                  key={ach.id} 
                  className="bg-white rounded-lg shadow-lg dark:bg-gray-800 p-6 transition-all duration-300 hover:shadow-xl self-start" // <- Tambahkan self-start di sini
                >
                  <div className="flex flex-col">
                    {/* {ach.images && ach.images.length > 0 && ( */}
                      <div className="h-64 mb-4">
                        <Carousel>
                          {ach.images && ach.images.length > 0 ? ach.images.map((image, idx) => (
                            <img
                              key={idx}
                              src={image.publicUrl}
                              alt={`Achievement ${idx + 1}`}
                              className="w-full h-full object-cover rounded-lg"
                            />
                          ))
                          :
                          <img
                            src="https://img.freepik.com/free-vector/teamwork-team-success-concept_74855-6673.jpg?semt=ais_hybrid&w=740"
                            alt="Placeholder"
                            className="w-full h-full object-cover rounded-lg"
                          />
                        }
                        </Carousel>
                      </div>
                    {/* )} */}
                    
                    <div className="flex-1">
                      <div className="flex flex-col mb-2">
                        <h2 className="md:text-lg text-md font-bold text-gray-800 dark:text-white">
                          {ach.name}
                        </h2>
                      </div>
                      
                      <div className="flex items-center text-gray-600 dark:text-gray-300">
                        <svg
                          className="w-5 h-5 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          style={{ minWidth: '20px' }}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                          />
                        </svg>
                        <span className='text-sm'>{ach.organizer}</span>
                      </div>

                      <div className='flex justify-between content-center my-2'>
                        <span className="text-sm text-gray-500 content-center">
                            {new Date(ach.date).toLocaleDateString('en-GB', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric'
                            })}
                        </span>
                        <span className="capitalize p-2 bg-blue-100 rounded-lg font-bold text-xs">{ach.level}</span>
                      </div>


                      {ach.description && (
                        <p className="mb-4 text-gray-700 dark:text-gray-300 truncate">
                          {ach.description}
                        </p>
                      )}

                      <div className="mt-4 flex flex-wrap gap-2">
                        <Button 
                          color="red" 
                          onClick={(e) => { e.stopPropagation(); handleDelete(ach.id); }}
                          size="sm"
                          className="flex-1 md:flex-none"
                        >
                          Delete
                        </Button>
                        <Button 
                          color="blue" 
                          onClick={(e) => { e.stopPropagation(); handleEdit(ach); }}
                          size="sm"
                          className="flex-1 md:flex-none"
                        >
                          Edit
                        </Button>
                        {ach.certificate_link && (
                          <a
                            href={ach.certificate_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-3 py-2 text-sm font-medium text-center text-white bg-green-600 rounded-lg hover:bg-green-700 focus:ring-4 focus:outline-none focus:ring-green-300 dark:bg-green-500 dark:hover:bg-green-600 dark:focus:ring-green-800 flex-1 md:flex-none justify-center"
                          >
                            View Certificate
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Modal for Adding/Editing Achievement */}
        <Modal show={openModal} onClose={onCloseModal} popup className="backdrop-blur-sm bg-white/30">
          <Modal.Header />
          <Modal.Body>
            <form onSubmit={handleSubmit} className="space-y-6 p-5">
              <h3 className="text-xl font-medium text-gray-900 dark:text-white">
                {isEditMode ? 'Edit Achievement' : 'Add New Achievement'}
              </h3>
              <div>
                <Label htmlFor="name" value="Achievement Name *" />
                <TextInput 
                  id="name" 
                  placeholder="e.g. National Data Science Competition Winner" 
                  required 
                  value={formData.name} 
                  onChange={handleInputChange} 
                />
              </div>
              <div>
                <Label htmlFor="organizer" value="Organizer *" />
                <TextInput 
                  id="organizer" 
                  placeholder="e.g. Ministry of Education" 
                  required 
                  value={formData.organizer} 
                  onChange={handleInputChange} 
                />
              </div>
              <div>
                <Label htmlFor="date" value="Date *" />
                <Datepicker 
                  id="date" 
                  selected={date} 
                  onChange={(date) => setDate(date)} 
                  required 
                />
              </div>
              <div>
                <Label htmlFor="level" value="Level *" />
                <Select 
                  id="level" 
                  required 
                  value={formData.level} 
                  onChange={handleInputChange}
                >
                  <option value="">Select Level</option>
                  <option value="company">Company</option>
                  <option value="regional">Regional</option>
                  <option value="national">National</option>
                  <option value="international">International</option>
                </Select>
              </div>
              <div>
                <Label htmlFor="description" value="Description" />
                <Textarea 
                  id="description" 
                  placeholder="Describe your achievement and its significance" 
                  value={formData.description} 
                  onChange={handleInputChange} 
                />
              </div>
              <div>
                <Label htmlFor="certificate_link" value="Certificate Link" />
                <TextInput 
                  id="certificate_link" 
                  placeholder="e.g. https://drive.google.com/..." 
                  value={formData.certificate_link} 
                  onChange={handleInputChange} 
                />
              </div>
              <div>
                <Label htmlFor="images" value="Image Documentation (Max 500KB per image)" />
                <input
                  id="images"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageChange}
                  className="block w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 cursor-pointer dark:text-gray-400 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400"
                />
                {imageError && <p className="mt-2 text-sm text-red-600 dark:text-red-500">{imageError}</p>}
                <div className="mt-4 space-y-2">
                  {images?.map((image, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex items-center">
                        {image.publicUrl && (
                          <img src={image.publicUrl} alt={`Preview ${index + 1}`} className="w-12 h-12 object-cover rounded mr-3" />
                        )}
                        <span className="text-sm text-gray-700 dark:text-gray-300 truncate max-w-xs">
                          {image.path ? image.path.split('/').pop() : image.name}
                        </span>
                      </div>
                      <Button 
                        color="red" 
                        size="xs" 
                        onClick={() => handleRemoveImage(image, index)}
                        className="ml-2"
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="w-full">
                <Button color="blue" type="submit" className="w-full">
                  {isEditMode ? 'Update Achievement' : 'Add Achievement'}
                </Button>
              </div>
            </form>
          </Modal.Body>
        </Modal>
      </section>
    </div>
  );
}

export default Achievement;